import { Entity } from '@core/entities/entity'
import { StatusColor } from '@core/theme/statuses'
import { IObservationBase } from '@core/entities/observation/ObservationBase/IObservationBase'
import {
  ObservationCoordinates,
  ObservationStatus,
  ObservationStatusHelpers,
} from '@core/entities/observation/types'
import { Priority, PriorityHelpers } from '@core/entities/priority'
import { User } from '@core/entities/user'
import { Type } from 'class-transformer'
import { Feature, Point } from 'geojson'
import circle from '@turf/circle'
import { point } from '@turf/helpers'
import type { Polygon, Units } from '@turf/helpers'

export const TRANSCRIBER_LANGUAGES = [
  { id: 'en', label: 'English', nativeLabel: 'English' },
  { id: 'sq', label: 'Albanian', nativeLabel: 'Shqip' },
  { id: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  { id: 'fr', label: 'French', nativeLabel: 'Français' },
  { id: 'it', label: 'Italian', nativeLabel: 'Italiano' },
  { id: 'lt', label: 'Lithuanian', nativeLabel: 'Lietuvių' },
  { id: 'pl', label: 'Polish', nativeLabel: 'Polski' },
  { id: 'ro', label: 'Romanian', nativeLabel: 'Română' },
  { id: 'ru', label: 'Russian', nativeLabel: 'Русский' },
  { id: 'so', label: 'Somali', nativeLabel: 'Soomaali' },
  { id: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { id: 'sv', label: 'Swedish', nativeLabel: 'Svenska' },
] as const

export class ObservationBase<
  ExtendedType extends IObservationBase = IObservationBase,
> extends Entity<ExtendedType> {
  id: number

  status: ObservationStatus

  title?: string

  priority?: Priority

  location?: string

  coordinates?: ObservationCoordinates

  language?: string

  createdOn?: string

  @Type(() => User)
  createdBy?: User

  public isProcessing() {
    return this.status === 'PROCESSING'
  }

  public getCoordinatesPoint(): Feature<Point> {
    if (!this.coordinates) return
    const { latitude, longitude, ...properties } = this.coordinates
    return point([longitude, latitude], properties)
  }

  public getCoordinatesAccuracyRadius(): Feature<Polygon> {
    if (!this.coordinates?.accuracy) return
    return circle(this.getCoordinatesPoint(), this.coordinates.accuracy, {
      steps: 50,
      units: 'meters' as Units,
    })
  }

  public getLanguageLabel() {
    return TRANSCRIBER_LANGUAGES.find((l) => l.id === this.language)?.label
  }

  public getPriorityLabel() {
    return PriorityHelpers.getLabel(this.priority)
  }

  public getPriorityColor() {
    return PriorityHelpers.getColor(this.priority)
  }

  public static statusChipColor(status: ObservationStatus): StatusColor {
    return ObservationStatusHelpers.getChipColor(status)
  }

  public static statusLabel(status: ObservationStatus): string {
    return ObservationStatusHelpers.getLabel(status)
  }

  public getStatusChipColor() {
    return ObservationStatusHelpers.getChipColor(this.status)
  }

  public getStatusLabel() {
    return ObservationStatusHelpers.getLabel(this.status)
  }
}
